import {AxiosInstance} from "axios";
import {
    BaseSurveyQuestion,
    PrefilledSurveyAnswer,
    Survey,
    SurveyAnswerInstance
} from "@/core/interfaces/survey/Survey";

export interface SurveyIndexRequest {
    /** Retrieve only surveys that have attached pathway. */
    pathway?: boolean;
    /** Scope the survey with slug */
    slug?: string;
    /** When set, the response will contain meta key with the latest instance done for the consumer key. */
    consumer_key?: string;
}

interface SurveyIndexResponse {
    data: Survey[];
    meta?: {
        latest_instances?: { [id: string]: { completed_at: string } }
    };
}

/**
 * The survey resource.
 *
 * @param {Object} client - The injected client.
 * @param {Object} hdsClient - The injected client.
 * @returns {Object} The public object.
 */
export default function (client: AxiosInstance, hdsClient: AxiosInstance) {
    return {
        /**
         * Get surveys.
         *
         * @param {Object} params - The query parameters.
         *
         * @returns {Promise} Promise object represents the HTTP request.
         */
        async index(params: SurveyIndexRequest = {}): Promise<SurveyIndexResponse> {
            return await client.get('/surveys', {params})
                .then(response => response.data);
        },

        /**
         * Read a complete survey with all its questions and answers.
         *
         * @param {Number} surveyId
         * @returns {Promise<Object>}
         */
        readBuiltSurvey(surveyId: number): Promise<Survey> {
            return client.get(`/surveys/${surveyId}/built`)
                .then(response => response.data.data);
        },

        /**
         * Read a built survey by his slug.
         */
        async readBuiltSurveyBySlug(slug: string): Promise<Survey> {
            return client.get(`surveys/slug/${slug}/built`)
                .then(response => response.data.data);
        },

        /**
         * Read the answers the API is able to prefill for a consumer.
         *
         * One entry per question of the survey, the answer being null when
         * nothing could be prefilled.
         *
         * @param surveyId The survey id.
         * @param consumerKey The consumer key.
         */
        async prefilledAnswers(surveyId: number, consumerKey: string): Promise<PrefilledSurveyAnswer[]> {
            return client.get(`/surveys/${surveyId}/prefilled-answers`, {params: {consumer_key: consumerKey}})
                .then(response => response.data.data);
        },

        /**
         * Create a new survey instance for a consumer.
         *
         * @param surveyId The survey id used for created the survey instance.
         * @param consumerKey The consumer key.
         *
         * @returns {Promise}
         */
        createInstance(surveyId: number, consumerKey: string) {
            return client.post(`/surveys/${surveyId}/answer-instances`, {consumer_key: consumerKey})
                .then(response => response.data.data);
        },

        /**
         * Read next question.
         *
         * @param surveyId The survey id.
         * @param instanceId The survey instance id.
         *
         * @returns {Promise}
         */
        next(surveyId: number, instanceId: number) {
            return client.get(`/surveys/${surveyId}/instances/${instanceId}/next`)
                .then(response => response);
        },

        /**
         * Answer a question of a survey instance.
         *
         * @param surveyId The survey id.
         * @param instanceId The survey instance id.
         * @param answers The answers to the current question in the instance (must be an array).
         *
         * @returns {Promise}
         */
        async answer(surveyId: number, instanceId: number, answers: BaseSurveyQuestion["user_answer"]) {
            return client.post(`/surveys/${surveyId}/instances/${instanceId}/answers`, answers)
                .then(response => response);
        },

        /**
         * Answer all questions of a survey instance.
         * @param surveyId  The survey id.
         * @param instanceId  The survey instance id.
         * @param answers  The answers.
         * @param completed_at  The completed at date.
         */
        async answerAll(
            surveyId: number,
            instanceId: number, answers: { question_id: number, value: (string | number | boolean | undefined)[] }[],
            completed_at?: string
        ): Promise<SurveyAnswerInstance> {
            return await client.patch(`/surveys/${surveyId}/instances/${instanceId}`, {
                answers,
                ...completed_at ? {completed_at: new Date().toISOString()} : {}
            }).then(response => response.data.data);
        },

        /**
         * Get the verdict of a survey instance.
         *
         * @param surveyId Survey id.
         * @param instanceId Instance id.
         *
         * @returns {Promise}
         */
        async verdict(surveyId: number, instanceId: number) {
            return client.get(`/surveys/${surveyId}/instances/${instanceId}/verdict`)
                .then(response => response.data);
        },

        /**
         * Get built survey answered instance.
         *
         * @param {number} instanceId
         *
         * @return {Promise}
         */
        async builtAnsweredInstance(instanceId: number): Promise<SurveyAnswerInstance> {
            return client.get(`survey-answer-instances/${instanceId}/built`)
                .then(response => response.data.data);
        },

        /**
         * Get an answer image.
         *
         * @param {number} surveyId
         * @param {number} questionId
         * @param {number} answerId
         *
         * @return {Promise<Object>}
         */
        answerImage(surveyId: number, questionId: number, answerId: number) {
            return client.get(`surveys/${surveyId}/questions/${questionId}/answers/${answerId}/image`, {responseType: 'blob'})
                .then(response => response.data);
        },

        /**
         * Get a question image.
         *
         * @param {number} surveyId
         * @param {number} questionId
         *
         * @return {Promise<Object>}
         */
        questionImage(surveyId: number, questionId: number) {
            return client.get(`surveys/${surveyId}/questions/${questionId}/image`, {responseType: 'blob'})
                .then(response => response.data);
        },

        /**
         * Export a survey instance as a pdf.
         *
         * @param surveyInstanceId
         */
        export(surveyInstanceId: string) {
            return hdsClient.get(`survey-answer-instances/${surveyInstanceId}/export`, {responseType: 'blob'})
                .then(response => response.data);
        }
    };
}
